<template>
    <record-editor
        id="contact-editor"
        :record="edited_record"
        :title="page_title"
        :can-view-data="can_view_record_data"
        :can-edit-data="can_edit_record_data"
        :can-delete-data="can_delete_data"
        @remove="confirm_record_delete"
    >
        <template v-slot:title>
            <div class="uk-margin-bottom lam-page-title" data-uk-sticky></div>
        </template>
        <div v-if="!!edited_record" slot="right-panel">
            <div class="uk-grid uk-grid-small" data-uk-grid-margin>
                <div class="uk-width-1-3">
                    <contact-avatar
                        :contact="edited_record"
                        :editable="editing"
                        :required="false"
                    />
                </div>
                <div class="uk-width-2-3">
                    <div v-if="editing" class="uk-grid uk-grid-small" data-uk-grid-margin>
                        <div class="uk-width-1-2">
                            <form-input
                                id="first_name"
                                v-model="editing_record.first_name"
                                name="first_name"
                                :required="true"
                                placeholder="First Name"
                                width="1-1"
                                :error="has_error('first_name')"
                            />
                        </div>
                        <div class="uk-width-1-2">
                            <form-input
                                id="last_name"
                                v-model="editing_record.last_name"
                                name="last_name"
                                :required="true"
                                placeholder="Last Name"
                                width="1-1"
                                :error="has_error('last_name')"
                            />
                        </div>

                        <div class="uk-width-1-1">
                            <firm-picker
                                :value="firm ? firm.id : 0"
                                @pick="editing_record.firm_id = $event.id"
                            />
                        </div>

                        <div class="uk-width-1-1">
                            <form-input
                                id="city_state"
                                v-model="editing_record.city_state"
                                name="city_state"
                                :required="false"
                                placeholder="City, State, Country"
                                width="1-1"
                                :error="has_error('city_state')"
                            />
                        </div>
                        <div class="uk-width-1-1">
                            <form-input
                                id="title"
                                v-model="editing_record.title"
                                name="title"
                                :required="false"
                                placeholder="Title"
                                width="1-1"
                                :error="has_error('title')"
                            />
                        </div>
                        <div class="uk-width-1-2">
                            <button
                                type="button"
                                class="uk-button uk-button-small uk-width-1-1"
                                @click.prevent="stop_editing(false)"
                            >
                                Cancel
                            </button>
                        </div>
                        <div class="uk-width-1-2">
                            <button
                                type="button"
                                class="uk-button uk-button-small uk-button-lam-orange uk-width-1-1"
                                @click.prevent="stop_editing(true)"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                    <div v-else>
                        <h3 class="contact-display-name uk-text-bold uk-margin-small-bottom">{{ edited_record.name }}</h3>
                        <firm-picker :value="firm ? firm.id : 0" :editable="false" />
                        <div v-if="edited_record.city_state">{{ edited_record.city_state }}</div>
                        <div v-if="edited_record.title">{{ edited_record.title }}</div>
                        <button
                            v-if="can_edit_record_data"
                            type="button"
                            class="uk-button uk-button-link uk-button-small uk-text-italic"
                            style="padding: 0; display: block;"
                            @click.prevent="start_editing"
                        >
                            (edit)
                        </button>
                    </div>
                </div>
            </div>

            <div class="uk-grid uk-grid-small" data-uk-grid-margin>
                <div class="uk-width-1-1">
                    <form-tags-input
                        id="position-filters"
                        name="position_filter_ids"
                        v-model="edited_record.position_filter_ids"
                        :local-options="position_filters"
                        placeholder="Position Filter(s)"
                        :required="false"
                        :editable="can_edit_record_data"
                        class="uk-width-1-1"
                        @input="save"
                    />
                </div>
            </div>

            <div class="uk-grid uk-grid-small" data-uk-grid-margin>
                <div class="uk-width-1-1">
                    <form-tags-input
                        id="tags"
                        name="tag_ids"
                        v-model="edited_record.tag_ids"
                        :local-options="tags"
                        placeholder="Tag(s)"
                        :required="false"
                        :editable="can_edit_record_data"
                        :can-add="true"
                        class="uk-width-1-1"
                        @input="save_tags"
                    />
                </div>
            </div>

            <contact-info
                entity="contact"
                :owner-id="edited_record.id"
                owner-type="App.Contact"
                :editable="can_edit_record_data"
            />

            <div v-if="!!edited_record.linkedin_profile_url || can_edit_record_data" class="uk-grid uk-grid-small" data-uk-grid-margin>
                <div class="uk-flex uk-width-1-1">
                    <a v-if="!can_edit_record_data && !!edited_record.linkedin_profile_url" :href="edited_record.linkedin_profile_url" target="_blank" class="uk-button uk-width-1-1">
                        {{ edited_record.linkedin_profile_url }}
                        <open-icon glyph="external-link" />
                    </a>
                    <form-input
                        v-else
                        id="linkedin_profile_url"
                        v-model="edited_record.linkedin_profile_url"
                        name="linkedin_profile_url"
                        :required="false"
                        placeholder="LinkedIn Profile"
                        width="1-1"
                        :error="has_error('linkedin_profile_url')"
                        class="uk-width-1-1"
                        @input="save"
                    />
                    <div v-if="can_edit_record_data && !!edited_record.linkedin_profile_url" class="uk-margin-small-left uk-flex-item-none">
                        <a :href="edited_record.linkedin_profile_url" target="_blank" class="uk-button">
                            <open-icon glyph="external-link" />
                        </a>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="!!edited_record" slot="left-panel">
            <div class="uk-accordion">
                <h3 class="uk-accordion-title lam-tight">
                    Projects
                </h3>
                <div class="uk-accordion-content lam-tight">
                    <project-data-table
                        id="firm-projects"
                        context="contact"
                        :context-id="edited_record.id"
                    />
                </div>

                <h3 class="uk-accordion-title lam-tight uk-margin-large-top">
                    Prospective Projects
                </h3>
                <div class="uk-accordion-content lam-tight">
                    <project-data-table
                        id="firm-prospective-projects"
                        context="contact"
                        :context-id="edited_record.id"
                        :active="false"
                    />
                </div>

                <div class="uk-accordion uk-margin-large-top">
                    <h3 class="uk-accordion-title">
                        Notes
                    </h3>
                    <div class="uk-accordion-content lam-tight">
                        <comments
                            :owner-id="edited_record.id || 0"
                            owner-type="App.Contact"
                        />
                    </div>
                </div>
            </div>
        </div>
    </record-editor>
</template>

<script type="text/javascript">
import { mapGetters } from "vuex"

import is_record_editor from "../../mixins/IsRecordEditor"
import string_utilities from "../../mixins/StringUtilities"

import FormInput from "../../ui/FormInput"
import FormTagsInput from "../../ui/FormTagsInput"
import FirmPicker from "../../ui/FirmPicker"
import ContactInfo from "../../ui/ContactInfo"
import ContactAvatar from "../../ui/ContactAvatar"
import Comments from "../../ui/Comments"
import ProjectDataTable from "../../ui/DataTable/ProjectDataTable"
import OpenIcon from "../../ui/OpenIcon"

export default {
    mixins: [is_record_editor, string_utilities],
    components: {
        FormInput,
        FirmPicker,
        FormTagsInput,
        ContactInfo,
        ContactAvatar,
        Comments,
        ProjectDataTable,
        OpenIcon
    },
    methods: {
        save_tags (selection) {
            const value = []
            const new_terms = []
            selection.forEach(option => {
                if((typeof option === "object")) {
                    if(option.id) value.push(option.id)
                    else new_terms.push(option.name)
                } else value.push(option)
            })
            this.save(value, "tag_ids")
            const add_new_term = () => {
                if(new_terms.length) {
                    this.$store.dispatch("CREATE_RECORD", {
                        entity: "term",
                        data: { dictionary_id: "AS_contact_tags", name: new_terms[0] }
                    }).then(term => {
                        if(term.id) {
                            value.push(term.id)
                            this.save(value, "tag_ids")
                        }
                        new_terms.splice(0, 1)
                        add_new_term()
                    })
                }
            }
            add_new_term()
        },
        start_editing () {
            this.editing_record = { ...this.edited_record }
            this.editing = true
        },
        stop_editing (save) {
            if(save) {
                this.clear_all_errors()
                const data = {
                    first_name: this.editing_record.first_name,
                    last_name: this.editing_record.last_name,
                    firm_id: this.editing_record.firm_id,
                    city_state: this.editing_record.city_state,
                    title: this.editing_record.title
                }
                return this.save_data_for_record_id("contact", this.edited_record.id, data).then(() => {
                    this.editing = false
                })
            }
            this.editing = false
        }
    },
    computed: {
        ...mapGetters(["entity_records"]),
        page_title () {
            if(!this.edited_record || !this.edited_record.name) return (this.edited_record && this.edited_record.id) ? "Contact" : "New Contact"
            return this.edited_record.name // *** firm name?
        },
        raw_firm_records () {
            return this.entity_records("firm")
        },
        firm () {
            if(this.editing_record) return this.raw_firm_records.find(firm => firm.id === this.editing_record.firm_id) || { id: 0 }
            if(this.edited_record) return this.raw_firm_records.find(firm => firm.id === this.edited_record.firm_id) || { id: 0 }
            return {}
        },
        position_filters () {
            return this.dictionary("position_filter", "name")
        },
        tags () {
            return this.dictionary("AS_contact_tags", "name")
        }
    },
    data () {
        return {
            entity: "contact",
            relation_ids: ["contact_method", "firm", "comment"],
            editing_record: null,
            editing: false
        }
    }
}
</script>
